<template>
  <div class="screenshare-session d-flex flex-column vh-100 bg-dark">
    <div v-if="!screenShareUrl" class="flex-grow-1 d-flex align-items-center justify-content-center text-white">
      <div class="text-center px-3">
        <i class="fa fa-exclamation-triangle fa-2x mb-3"></i>
        <p class="mb-0">No screenshare session link found. Start screenshare from the inbox again.</p>
      </div>
    </div>

    <iframe
      v-else
      ref="upscopeIframe"
      :src="screenShareUrl"
      class="flex-grow-1 w-100 border-0"
      title="Screenshare session"
      allow="microphone *; camera *; autoplay *; display-capture *; clipboard-write *; screen-wake-lock *"
      allowfullscreen
      @load="onIframeLoad"
    ></iframe>
  </div>
</template>

<script>
const RETURN_URL_KEY = 'screenshare_return_url';
const BC_NAME = 'modal_screenshare';

function upscopeDebug(step, detail) {
  try {
    if (detail === undefined) {
      console.log('[upscope-debug][agent-screenshare]', step);
    } else {
      console.log('[upscope-debug][agent-screenshare]', step, detail);
    }
  } catch (e) {
    // ignore
  }
}

export default {
  name: 'Screenshare',
  data() {
    return {
      screenShareUrl: '',
      sessionEndedHandled: false,
      lastAudioState: null,
      lastSessionStatus: null,
      lastVideoVisitorState: null,
      lastVideoAgentState: null,
    };
  },
  mounted() {
    const storedUrl = localStorage.getItem('screenshare_watch_link');
    this.screenShareUrl = storedUrl || '';
    upscopeDebug('mounted: agent screenshare shell', {
      has_watch_link: !!storedUrl,
      watch_url_host: (() => {
        try {
          return storedUrl ? new URL(storedUrl).host : '';
        } catch (e) {
          return 'invalid';
        }
      })(),
      has_return_url: !!localStorage.getItem(RETURN_URL_KEY),
      has_session_ctx: !!localStorage.getItem('screenshare_session_ctx'),
    });
    setTimeout(() => {
      localStorage.removeItem('screenshare_watch_link');
      upscopeDebug('mounted: cleared screenshare_watch_link from localStorage');
    }, 1000);
    window.addEventListener('message', this.onUpscopeMessage);
    upscopeDebug('mounted: listening for Upscope/session-end messages');
  },
  beforeUnmount() {
    upscopeDebug('beforeUnmount: removing message listener');
    window.removeEventListener('message', this.onUpscopeMessage);
  },
  methods: {
    onIframeLoad() {
      const frame = this.$refs.upscopeIframe;
      if (!frame) {
        upscopeDebug('onIframeLoad: no iframe ref');
        return;
      }
      if (!frame.getAttribute('allow')) {
        frame.setAttribute(
          'allow',
          'microphone *; camera *; autoplay *; display-capture *; clipboard-write *; screen-wake-lock *',
        );
      }

      // When Upscope branding.on_end_url loads /screenshare-ended in the iframe,
      // the iframe becomes same-origin and we can detect it on load.
      try {
        const href = frame.contentWindow?.location?.href || '';
        upscopeDebug('onIframeLoad: iframe loaded', {
          same_origin_href: href || '(empty)',
          is_ended_page: href.includes('/screenshare-ended'),
        });
        if (href.includes('/screenshare-ended')) {
          this.returnToOriginTab('iframe_on_end_url');
        }
      } catch (e) {
        upscopeDebug('onIframeLoad: still cross-origin (live Upscope session)', {
          message: e && e.message,
        });
      }
    },
    onUpscopeMessage(event) {
      const data = event.data || {};
      const iframeWindow = this.$refs.upscopeIframe?.contentWindow;

      const fromOurIframe = iframeWindow && event.source === iframeWindow;
      const fromSameOrigin =
        typeof event.origin === 'string' && event.origin === window.location.origin;
      // Nested Upscope frames may postMessage with a nested source; accept Upscope origins too.
      let fromUpscope = false;
      try {
        if (typeof event.origin === 'string') {
          const host = new URL(event.origin).hostname;
          fromUpscope = host === 'upscope.io' || host.endsWith('.upscope.io') || host.endsWith('cobrowsingapi.com');
        }
      } catch (e) {
        fromUpscope = false;
      }

      if (!fromOurIframe && !fromSameOrigin && !fromUpscope) {
        return;
      }

      // Agent iframe events (esp. audio) come via upscope.sessionState.update when Upscope posts them.
      // audioState: off | ringing | accepted | authorizing | active
      // https://cobrowsingapi.com/docs/rest-api/embedded-session-control
      upscopeDebug('onUpscopeMessage: accepted message', {
        origin: event.origin,
        fromOurIframe,
        fromSameOrigin,
        fromUpscope,
        data_type: data.type || null,
        data_event: data.event || null,
        sessionStatus: data.state?.sessionStatus || null,
        audioState: data.state?.audioState || null,
        viewerAudioAvailable: data.state?.viewerAudioAvailable,
        visitorAudioAvailable: data.state?.visitorAudioAvailable,
        muted: data.state?.muted,
        videoVisitorState: data.state?.videoVisitorState || null,
        videoAgentState: data.state?.videoAgentState || null,
        error: data.error || null,
        data,
      });

      if (data.type === 'error') {
        upscopeDebug('AUDIO/CONNECTION error from Upscope iframe', {
          error: data.error,
        });
      }

      if (data.type === 'upscope.sessionState.update' && data.state) {
        const state = data.state;
        if (state.audioState && state.audioState !== this.lastAudioState) {
          upscopeDebug('AUDIO state transition', {
            from: this.lastAudioState,
            to: state.audioState,
            meaning: {
              off: 'audio off / declined / ended / never started',
              ringing: 'audio call REQUESTED (ringing visitor)',
              accepted: 'visitor ACCEPTED audio call',
              authorizing: 'mic permission / authorizing',
              active: 'audio P2P ACTIVE',
            }[state.audioState] || state.audioState,
            viewerAudioAvailable: state.viewerAudioAvailable,
            visitorAudioAvailable: state.visitorAudioAvailable,
            muted: state.muted,
          });
          // ringing → off without accepted/active ≈ declined or failed before connect
          if (
            this.lastAudioState === 'ringing' &&
            state.audioState === 'off'
          ) {
            upscopeDebug('AUDIO likely DECLINED or FAILED before accept', {
              from: this.lastAudioState,
              to: state.audioState,
            });
          }
          if (
            (this.lastAudioState === 'authorizing' || this.lastAudioState === 'accepted') &&
            state.audioState === 'off'
          ) {
            upscopeDebug('AUDIO likely FAILED during authorize/accept (mic blocked?)', {
              from: this.lastAudioState,
              to: state.audioState,
            });
          }
          this.lastAudioState = state.audioState;
        }
        if (state.sessionStatus && state.sessionStatus !== this.lastSessionStatus) {
          upscopeDebug('sessionStatus transition', {
            from: this.lastSessionStatus,
            to: state.sessionStatus,
          });
          this.lastSessionStatus = state.sessionStatus;
        }
        if (state.videoVisitorState && state.videoVisitorState !== this.lastVideoVisitorState) {
          upscopeDebug('VIDEO visitor state transition', {
            from: this.lastVideoVisitorState,
            to: state.videoVisitorState,
          });
          this.lastVideoVisitorState = state.videoVisitorState;
        }
        if (state.videoAgentState && state.videoAgentState !== this.lastVideoAgentState) {
          upscopeDebug('VIDEO agent state transition', {
            from: this.lastVideoAgentState,
            to: state.videoAgentState,
          });
          this.lastVideoAgentState = state.videoAgentState;
        }
      }

      if (data.type === 'sessionEnded' || data.event === 'screenshare_session_ended') {
        this.returnToOriginTab('postMessage');
        return;
      }

      if (
        data.type === 'upscope.sessionState.update' &&
        data.state &&
        data.state.sessionStatus === 'terminated'
      ) {
        this.returnToOriginTab('sessionState');
      }
    },
    notifyInboxTab() {
      upscopeDebug('notifyInboxTab: broadcasting session ended to inbox');
      try {
        const bc = new BroadcastChannel(BC_NAME);
        bc.postMessage({ type: 'screenshare_session_ended' });
        bc.close();
        upscopeDebug('notifyInboxTab: BroadcastChannel posted');
      } catch (e) {
        upscopeDebug('notifyInboxTab: BroadcastChannel unsupported', { message: e && e.message });
      }
      try {
        localStorage.setItem('screenshare_session_ended_at', String(Date.now()));
        upscopeDebug('notifyInboxTab: localStorage ended_at set');
      } catch (e) {
        upscopeDebug('notifyInboxTab: localStorage failed', { message: e && e.message });
      }
    },
    returnToOriginTab(reason) {
      if (this.sessionEndedHandled) {
        upscopeDebug('returnToOriginTab: already handled, skip', { reason });
        return;
      }
      this.sessionEndedHandled = true;
      upscopeDebug('returnToOriginTab: CONNECTION ENDED', {
        reason,
        has_opener: !!(window.opener && !window.opener.closed),
      });

      const returnUrl = localStorage.getItem(RETURN_URL_KEY) || '';
      this.notifyInboxTab();

      try {
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.focus();
            upscopeDebug('returnToOriginTab: focused opener inbox tab');
          } catch (e) {
            upscopeDebug('returnToOriginTab: opener.focus failed', { message: e && e.message });
          }
          window.close();
          setTimeout(() => {
            if (!window.closed) {
              upscopeDebug('returnToOriginTab: window did not close, navigating', {
                returnUrl: returnUrl || '/admin/chatinbox?tab=inbox',
              });
              window.location.href = returnUrl || '/admin/chatinbox?tab=inbox';
            }
          }, 500);
          return;
        }
      } catch (e) {
        upscopeDebug('returnToOriginTab: opener path error', { message: e && e.message });
      }

      upscopeDebug('returnToOriginTab: navigating current tab', {
        returnUrl: returnUrl || '/admin/chatinbox?tab=inbox',
      });
      window.location.href = returnUrl || '/admin/chatinbox?tab=inbox';
    },
  },
};
</script>

<style scoped>
.screenshare-session {
  min-height: 100vh;
}
</style>
